import React from 'react'
import PropTypes from 'prop-types'
import * as Accordion from '@radix-ui/react-accordion'
import {ChevronDownIcon, ChevronRightIcon} from 'lucide-react'
import Link from '~/app/components/link'

const FONT_SIZE_MAP = {
    lg: 'text-lg',
    md: 'text-base',
    sm: 'text-sm'
}

const FONT_WEIGHT_MAP = {
    semibold: 'font-semibold',
    bold: 'font-bold',
    regular: 'font-normal',
    medium: 'font-medium'
}

/**
 * Radix-based replacement for the Chakra NestedAccordion.
 * Same prop interface as the original for drop-in compatibility.
 */
const NestedAccordion = (props) => {
    const {
        item,
        initialDepth = 0,
        itemsFilter = () => true,
        itemsAfter,
        itemsBefore,
        itemsKey = 'items',
        itemsCountKey = 'count',
        fontWeights = [],
        fontSizes = [],
        urlBuilder = (item) => `/${item.id}`,
        allowMultiple,
        ...rest
    } = props

    const depth = initialDepth
    const items = item[itemsKey] || []

    const filter = (item) =>
        typeof itemsFilter === 'function' ? itemsFilter(item) : !!item[itemsFilter]

    const ItemComponent = props?.itemComponent || NestedAccordion

    const fontSizeClass = FONT_SIZE_MAP[fontSizes[depth]] || 'text-base'
    const fontWeightClass = FONT_WEIGHT_MAP[fontWeights[depth]] || 'font-normal'
    const textClasses = `${fontSizeClass} ${fontWeightClass}`

    return (
        <Accordion.Root type="multiple" className="sf-nested-accordion" {...rest}>
            {/* Optional accordion items before others in items list. */}
            {typeof itemsBefore === 'function' ? itemsBefore({item, depth}) : itemsBefore}

            {items.filter(filter).map((item) => {
                const {id, name} = item
                const itemsCount = item[itemsCountKey] || item[itemsKey]?.length || 0

                return (
                    <Accordion.Item key={id} value={id} className="border-none">
                        <h2>
                            {itemsCount > 0 ? (
                                <Accordion.Trigger className="flex w-full items-center gap-2 py-2 pl-2 text-left hover:no-underline group">
                                    <ChevronRightIcon className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:hidden" />
                                    <ChevronDownIcon className="h-4 w-4 shrink-0 transition-transform duration-200 hidden group-data-[state=open]:block" />
                                    <span className={textClasses}>{name}</span>
                                </Accordion.Trigger>
                            ) : (
                                <Link
                                    to={urlBuilder(item)}
                                    className={`flex w-full items-center py-2 pl-8 text-left no-underline hover:underline ${textClasses}`}
                                >
                                    {name}
                                </Link>
                            )}
                        </h2>

                        {itemsCount > 0 && (
                            <Accordion.Content className="overflow-hidden pl-4 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                                <ItemComponent
                                    {...props}
                                    item={item}
                                    itemsKey={itemsKey}
                                    itemsCountKey={itemsCountKey}
                                    initialDepth={depth + 1}
                                    itemComponent={NestedAccordion}
                                />
                            </Accordion.Content>
                        )}
                    </Accordion.Item>
                )
            })}

            {/* Optional accordion items after others in items list. */}
            {typeof itemsAfter === 'function' ? itemsAfter({item, depth}) : itemsAfter}
        </Accordion.Root>
    )
}

NestedAccordion.displayName = 'NestedAccordion'

NestedAccordion.propTypes = {
    item: PropTypes.object.isRequired,
    itemsAfter: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    itemsBefore: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    initialDepth: PropTypes.number,
    itemComponent: PropTypes.elementType,
    itemsKey: PropTypes.string,
    itemsCountKey: PropTypes.string,
    itemsFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    fontSizes: PropTypes.array,
    fontWeights: PropTypes.array,
    urlBuilder: PropTypes.func,
    allowMultiple: PropTypes.bool
}

export default NestedAccordion
